import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useCoinBalance } from "@/lib/coin/coin.store";
import { useSubscription } from "@/lib/subscription/useSubscription.hook";
import { useAuth } from "@/lib/contexts/auth.context";
import { AnalyticsService } from "@/lib/analytics/analytics.service";
import { SubscriptionService } from "@/lib/subscription/subscription.service";

/**
 * Pricing Success Page - Shown after successful subscription purchase
 *
 * Displays success message, refreshes coin balance and subscription status,
 * then redirects to dashboard after a few seconds
 */
const PricingSuccessPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { refresh: refreshBalance } = useCoinBalance();
	const { refreshUsage } = useSubscription();
	const { refreshUserProfile, user } = useAuth();
	const hasRefreshed = useRef(false);

	const sessionId = searchParams.get("session_id");

	useEffect(() => {
		// If we're in a popup window (opened by parent), send message and close
		if (window.opener && !window.opener.closed && sessionId) {
			window.opener.postMessage(
				{ type: "STRIPE_PAYMENT_SUCCESS", sessionId },
				window.location.origin,
			);
			// Close popup immediately
			window.close();
			return;
		}

		// If we're in the main window (not popup), handle normally
		// Only refresh data once to avoid duplicate API calls
		if (!hasRefreshed.current) {
			hasRefreshed.current = true;

			const refreshData = async () => {
				try {
					await Promise.all([
						refreshBalance(),
						refreshUsage(),
						refreshUserProfile(),
					]);

					// After profile refresh, try to enrich the conversion event
					// with plan details (price, currency, plan name)
					let planName: string | undefined;
					let amount: number | undefined;
					let priceId: string | undefined;
					try {
						const plans = await SubscriptionService.getPlans();
						const currentPlan = plans.find((p) => p.id === user?.currentPlanId);
						if (currentPlan) {
							planName = currentPlan.name;
							amount = currentPlan.price ?? undefined;
							priceId = currentPlan.stripePriceId ?? undefined;
						}
					} catch {
						// Non-critical – still track without enrichment
					}

					// Track subscription purchase conversion with revenue data
					AnalyticsService.track("subscription_purchased", {
						session_id: sessionId,
						plan_name: planName,
						price_id: priceId,
						amount,
						currency: amount != null ? "EUR" : undefined,
					});
				} catch (error) {
					console.error("Failed to refresh data after purchase:", error);
					// Still track the conversion even if refresh fails
					AnalyticsService.track("subscription_purchased", {
						session_id: sessionId,
					});
				}
			};

			refreshData();
		}

		// Always set up redirect timer (even on remount in strict mode)
		const redirectTimer = setTimeout(() => {
			navigate("/dashboard");
		}, 5000);

		return () => clearTimeout(redirectTimer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="flex-1 overflow-y-auto pb-16 md:pb-8">
			<div className="flex min-h-[calc(100vh-8rem)] items-center justify-center px-4">
				<div className="w-full max-w-md text-center">
					<div className="mb-6 flex justify-center">
						<div className="rounded-full bg-emerald-500/10 p-4">
							<CheckCircle2 className="h-16 w-16 text-emerald-500" />
						</div>
					</div>

					<h1 className="mb-4 text-3xl font-semibold text-foreground">
						{t("pricing.subscription.success.title")}
					</h1>

					<p className="mb-8 text-muted-foreground">
						{t("pricing.subscription.success.message")}
					</p>

					<div className="mb-6 rounded-lg border bg-card p-4">
						<div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
							<Loader2 className="h-4 w-4 animate-spin" />
							<span>{t("pricing.subscription.success.refreshing")}</span>
						</div>
					</div>

					<p className="text-sm text-muted-foreground">
						{t("pricing.subscription.success.redirecting")}
					</p>
				</div>
			</div>
		</div>
	);
};

export default PricingSuccessPage;
